import { useState, useEffect, useMemo } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';

interface UseUserDataReturn {
  userData: any | null;
  loading: boolean;
}

/**
 * Custom hook to fetch the logged-in user's profile data from Firestore.
 *
 * Founder view-as: when `viewAsActive` is true on the AuthContext, the
 * returned `userData` has its `companyId` / `role` / `canSeeAllOffices`
 * fields swapped to mirror the viewAs target tenant. Every page that calls
 * `useUserData()` directly (e.g. report pages: SettlementAge, Clawback,
 * DischargedLoans, RefinanceIntelligence, Arrears) will then pass the
 * correct tenant's `companyId` to its data hooks instead of the founder's
 * own (`'system'`) — without needing the page to know about view-as.
 *
 * Identity fields (`uid`, `email`, `displayName`, etc.) are preserved so
 * audit logs and "who is logged in?" UI continue to reflect the founder.
 * Only the *scoping* fields are swapped. Non-viewAs sessions are byte-
 * identical (the synthesis short-circuits on `!viewAsActive`).
 *
 * @returns {{userData: object | null, loading: boolean}}
 */
export const useUserData = (): UseUserDataReturn => {
  const { user, viewAsActive, viewAsTargetCompanyId } = useAuth();
  const [rawUserData, setRawUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    if (user) {
      setLoading(true);
      const userDocRef = doc(db, 'users', user.uid);

      unsubscribe = onSnapshot(userDocRef, (docSnap: any) => {
        if (docSnap.exists()) {
          setRawUserData({ uid: docSnap.id, ...docSnap.data() });
        } else {
          setRawUserData(null);
        }
        setLoading(false);
      }, (error: any) => {
        setRawUserData(null);
        setLoading(false);
      });

    } else {
      // User is logged out
      setRawUserData(null);
      setLoading(false);
    }

    // Cleanup subscription on unmount or when user changes
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user]); // Re-run effect when the user object changes

  const userData = useMemo(() => {
    if (!rawUserData) return rawUserData;
    if (viewAsActive && viewAsTargetCompanyId) {
      return {
        ...rawUserData,
        companyId: viewAsTargetCompanyId,
        role: 'super_admin',
        canSeeAllOffices: true,
        assignedOffices: rawUserData.assignedOffices ?? [],
      };
    }
    // SECURITY (Marketing RBAC deny-by-default): the SPA must not act on stale
    // broker/commission-derived flags for a marketing user, even if the user
    // doc still carries them (pre-migration doc, or mid-refresh). Mirror the
    // server-side claim/doc strip: clear broker/commission scope fields, force
    // canSeeAllOffices + hasAccessRestrictions false, KEEP hideCommissions true.
    // view-as is founder-only (forced super_admin above) so the two are
    // mutually exclusive.
    if (rawUserData.role === 'marketing' || rawUserData.role === 'service_staff') {
      const {
        brokerDocId: _brokerDocId,
        brokerId: _brokerId,
        allowedEntityCodes: _allowedEntityCodes,
        linkedBrokerDocIds: _linkedBrokerDocIds,
        linkedLoanBookGrants: _linkedLoanBookGrants,
        visiblePaymentTypes: _visiblePaymentTypes,
        allowedReports: _allowedReports,
        allowedLoanBookIds: _allowedLoanBookIds,
        allowedTemplateIds: _allowedTemplateIds,
        ...rest
      } = rawUserData;
      return {
        ...rest,
        canSeeAllOffices: false,
        hasAccessRestrictions: false,
        hideCommissions: true,
      };
    }
    return rawUserData;
  }, [rawUserData, viewAsActive, viewAsTargetCompanyId]);

  return { userData, loading };
};